/**
 * Presence domain — the player reports its OWN online state.
 *
 * `POST /online/heartbeat` is the only producer of `monitor.online_users`,
 * which backs the backoffice Online tab and svc-monitor's multi-account
 * clusterer. The member is taken from the bearer token upstream, so nothing
 * identifying is sent from here.
 */
import { apiFetch } from '../../lib/api/client.js';
import { storageKey } from '../../lib/storage-key.js';

const DEVICE_ID_STORAGE = 'device-id';

/**
 * A random id minted once per browser profile and kept in localStorage.
 *
 * NOT a fingerprint: it is not derived from anything about the machine and a
 * cleared profile mints a new one. It exists so svc-monitor's `same_device`
 * clustering — several member accounts signing in from one browser — has an
 * input at all; `same_ip` alone cannot separate a shared household from a
 * shared NAT. Null (private mode, storage blocked) is a legitimate answer and
 * the heartbeat is still sent without it.
 */
export function deviceId(): string | null {
  try {
    const key = storageKey(DEVICE_ID_STORAGE);
    const existing = localStorage.getItem(key);
    if (existing) return existing;
    const minted = crypto.randomUUID();
    localStorage.setItem(key, minted);
    return minted;
  } catch {
    return null;
  }
}

export async function postPresenceHeartbeat(): Promise<void> {
  const fingerprint = deviceId();
  await apiFetch<unknown>('/online/heartbeat', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(fingerprint ? { deviceFingerprint: fingerprint } : {}),
  });
}
